import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { usePageTitle } from "../../hooks/usePageTitle";
import adminFetch from "../../utils/adminFetch";
import { useSocket } from "../../hooks/useSocket";
import AssignmentDropdown from "../../components/admin/AssignmentDropdown";
import ActivityNotes from "../../components/admin/ActivityNotes";

function timeAgo(str) {
  const diff = Date.now() - new Date(str).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60)  return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)   return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7)   return `${days}d ago`;
  return new Date(str).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const STATUS_OPTIONS = ["unread", "read", "replied"];
const STATUS_CONFIG  = {
  unread:  { label: "Unread",  color: "#92400E", bg: "#FEF3C7" },
  read:    { label: "Read",    color: "#1D4ED8", bg: "#DBEAFE" },
  replied: { label: "Replied", color: "#065F46", bg: "#D1FAE5" },
};

const PAGE_SIZE = 15;

function Spinner() {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "240px" }}>
      <svg className="animate-spin" width="28" height="28" viewBox="0 0 24 24"
        fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="2">
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────
function DetailModal({ m, onClose, onStatusChange, onDelete, onReplySent }) {
  const [replyText,  setReplyText]  = useState("");
  const [sending,    setSending]    = useState(false);
  const [sent,       setSent]       = useState(false);
  const [replyErr,   setReplyErr]   = useState(null);
  const [updating,   setUpdating]   = useState(false);
  const [deleting,   setDeleting]   = useState(false);
  const [assignedTo, setAssignedTo] = useState(m.assignedTo || {});

  const handleStatusChange = async (status) => {
    setUpdating(true);
    await onStatusChange(m._id, status);
    setUpdating(false);
  };

  const handleDelete = async () => {
    if (!window.confirm("Delete this message? This cannot be undone.")) return;
    setDeleting(true);
    await onDelete(m._id);
    setDeleting(false);
    onClose();
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    setReplyErr(null);
    try {
      const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/contacts/${m._id}/reply`, {
        method: "POST", body: JSON.stringify({ replyText }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || "Reply failed.");
      setSent(true);
      setReplyText("");
      onReplySent(m._id);
    } catch (err) {
      setReplyErr(err.message);
    } finally {
      setSending(false);
    }
  };

  const cfg = STATUS_CONFIG[m.status] || {};

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 50,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "24px",
        backgroundColor: "rgba(1,4,20,0.5)",
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "100%", maxWidth: "580px",
          backgroundColor: "#F7F4EF",
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(1,46,114,0.22)",
          border: "1px solid rgba(1,46,114,0.1)",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Header */}
        <div style={{
          background: "linear-gradient(135deg, #012E72, #002DB5)",
          padding: "22px 28px",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px",
          flexShrink: 0,
        }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.28em", textTransform: "uppercase", color: "rgba(255,255,255,0.5)", marginBottom: "5px" }}>
              Contact Message
            </p>
            <h2 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "17px", color: "#fff", letterSpacing: "0.04em", lineHeight: 1.2, marginBottom: "4px" }}>
              {m.name}
            </h2>
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(255,255,255,0.6)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {m.subject}
            </p>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexShrink: 0 }}>
            <span style={{
              fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.1em", textTransform: "uppercase",
              color: cfg.color, backgroundColor: cfg.bg,
              borderRadius: "6px", padding: "4px 10px", whiteSpace: "nowrap",
            }}>
              {cfg.label}
            </span>
            <button onClick={onClose} style={{
              background: "none", border: "none", cursor: "pointer",
              color: "rgba(255,255,255,0.45)", transition: "color 0.15s",
            }}
              onMouseEnter={e => e.currentTarget.style.color = "#fff"}
              onMouseLeave={e => e.currentTarget.style.color = "rgba(255,255,255,0.45)"}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", flex: 1 }}>
          <div style={{ padding: "22px 28px", display: "flex", flexDirection: "column", gap: "20px" }}>

            {/* Sender info */}
            <div>
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "12px" }}>
                Sender
              </p>
              <div style={{ display: "flex", flexDirection: "column" }}>
                {[
                  ["Email",    m.email,           "mailto:" + m.email],
                  ["Received", timeAgo(m.createdAt), null],
                ].map(([label, value, href]) => (
                  <div key={label} style={{ display: "flex", gap: "14px", alignItems: "baseline", padding: "9px 0", borderBottom: "1px solid rgba(1,46,114,0.07)" }}>
                    <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.14em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", flexShrink: 0, width: "68px" }}>{label}</span>
                    {href ? (
                      <a href={href} style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#002DB5", textDecoration: "none" }}
                        onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                        onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}>
                        {value}
                      </a>
                    ) : (
                      <span style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72" }}>{value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Message body */}
            <div>
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "10px" }}>
                Message
              </p>
              <p style={{ fontFamily: "'Constantia', serif", fontSize: "14px", color: "#012E72", lineHeight: 1.8, backgroundColor: "#fff", borderRadius: "12px", padding: "16px 18px", border: "1px solid rgba(1,46,114,0.09)", whiteSpace: "pre-wrap" }}>
                {m.message}
              </p>
            </div>

            {/* Status buttons */}
            <div>
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "10px" }}>
                Status
              </p>
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {STATUS_OPTIONS.map(s => {
                  const active = m.status === s;
                  const scfg   = STATUS_CONFIG[s];
                  return (
                    <button key={s}
                      onClick={() => !active && handleStatusChange(s)}
                      disabled={updating}
                      style={{
                        fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em",
                        textTransform: "uppercase", border: "none", borderRadius: "8px",
                        padding: "9px 18px", cursor: active ? "default" : "pointer",
                        backgroundColor: active ? scfg.bg    : "rgba(1,46,114,0.05)",
                        color:           active ? scfg.color : "rgba(1,46,114,0.35)",
                        fontWeight:      active ? 700 : 400,
                        outline:         active ? `2px solid ${scfg.color}30` : "none",
                        outlineOffset:   "2px",
                        opacity: updating ? 0.6 : 1,
                        transition: "all 0.15s",
                      }}
                      onMouseEnter={e => { if (!active && !updating) { e.currentTarget.style.backgroundColor = scfg.bg; e.currentTarget.style.color = scfg.color; } }}
                      onMouseLeave={e => { if (!active) { e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.05)"; e.currentTarget.style.color = "rgba(1,46,114,0.35)"; } }}
                    >
                      {scfg.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Reply composer */}
            <div>
              <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", marginBottom: "10px" }}>
                Reply to {m.email}
              </p>
              {sent ? (
                <div style={{ display: "flex", alignItems: "center", gap: "10px", padding: "12px 16px", borderRadius: "12px", backgroundColor: "#D1FAE5", border: "1px solid #6EE7B7" }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#065F46" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  <span style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "#065F46" }}>
                    Reply sent — status updated to <strong>Replied</strong>.
                  </span>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <textarea
                    rows={4}
                    placeholder="Write your reply…"
                    disabled={sending}
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    style={{
                      width: "100%", fontFamily: "'Constantia', serif", fontSize: "14px",
                      color: "#012E72", lineHeight: 1.7,
                      border: "1px solid rgba(1,46,114,0.14)", borderRadius: "12px",
                      padding: "12px 16px", outline: "none", resize: "vertical",
                      backgroundColor: "#fff",
                      boxSizing: "border-box",
                    }}
                    onFocus={e => e.target.style.borderColor = "#002DB5"}
                    onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.14)"}
                  />
                  {replyErr && (
                    <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "#dc2626" }}>{replyErr}</p>
                  )}
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      onClick={sendReply}
                      disabled={sending || !replyText.trim()}
                      style={{
                        fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em",
                        textTransform: "uppercase", color: "#fff",
                        background: "linear-gradient(135deg, #012E72, #002DB5)",
                        border: "none", borderRadius: "999px",
                        padding: "10px 22px", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: "8px",
                        boxShadow: "0 4px 16px rgba(0,45,181,0.25)",
                        opacity: sending || !replyText.trim() ? 0.4 : 1,
                        transition: "opacity 0.15s",
                      }}
                    >
                      {sending ? (
                        <>
                          <svg className="animate-spin" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                          </svg>
                          Sending…
                        </>
                      ) : (
                        <>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
                          </svg>
                          Send Reply
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

        {/* Assignment + Activity Notes */}
        <div style={{ padding: "0 28px 20px", display: "flex", flexDirection: "column", gap: "20px" }}>
          <AssignmentDropdown
            entityType="contacts"
            entityId={m._id}
            assignedTo={assignedTo}
            onAssigned={setAssignedTo}
          />
          <ActivityNotes
            entityType="contacts"
            entityId={m._id}
            notes={m.activityNotes || []}
          />
        </div>
        </div>

        {/* Footer */}
        <div style={{
          padding: "16px 28px", borderTop: "1px solid rgba(1,46,114,0.08)",
          backgroundColor: "rgba(1,46,114,0.02)",
          display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0,
        }}>
          <button
            onClick={handleDelete}
            disabled={deleting}
            style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.14em", textTransform: "uppercase", color: "#DC2626", border: "1px solid rgba(220,38,38,0.22)", borderRadius: "999px", padding: "8px 18px", cursor: deleting ? "not-allowed" : "pointer", backgroundColor: "transparent", opacity: deleting ? 0.4 : 1, transition: "all 0.15s" }}
            onMouseEnter={e => { if (!deleting) { e.currentTarget.style.backgroundColor = "#DC2626"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#DC2626"; } }}
            onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#DC2626"; e.currentTarget.style.borderColor = "rgba(220,38,38,0.22)"; }}
          >
            {deleting ? "Deleting…" : "Delete"}
          </button>
          <button onClick={onClose} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#fff", background: "linear-gradient(135deg, #012E72, #002DB5)", border: "none", borderRadius: "999px", padding: "9px 24px", cursor: "pointer", boxShadow: "0 4px 16px rgba(0,45,181,0.25)" }}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────
export default function AdminContacts() {
  usePageTitle("Contact Messages");
  const socketRef = useSocket();
  const navigate = useNavigate();
  const [items,      setItems]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [filter,     setFilter]     = useState("all");
  const [search,     setSearch]     = useState("");
  const [selected,   setSelected]   = useState(null);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [counts,     setCounts]     = useState({});
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo,   setDateTo]   = useState("");
  const searchTimer = useRef(null);

  const loadItems = useCallback(async (pg, srch, flt, from, to) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pg, limit: PAGE_SIZE });
      if (srch) params.set("search", srch);
      if (flt && flt !== "all") params.set("status", flt);
      if (from) params.set("from", from);
      if (to)   params.set("to", to);
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/contacts?${params}`);
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Failed to load contact messages.");
      const json = await res.json();
      setItems(json.data);
      setTotal(json.total);
      setTotalPages(json.pages);
      setCounts(json.counts || {});
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  }, [navigate]);

  useEffect(() => { loadItems(1, "", "all", "", ""); }, [loadItems]);

  useEffect(() => {
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => { setPage(1); loadItems(1, search, filter, dateFrom, dateTo); }, 300);
    return () => clearTimeout(searchTimer.current);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadItems(page, search, filter, dateFrom, dateTo); }, [filter, page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { setPage(1); loadItems(1, search, filter, dateFrom, dateTo); }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Socket listeners ──────────────────────────────────────────────
  useEffect(() => {
    const socket = socketRef?.current;
    if (!socket) return;

    function onNew(doc) {
      setItems(prev => {
        if (prev.some(m => m._id === doc._id)) return prev;
        return [doc, ...prev];
      });
    }

    function onUpdated({ id, status }) {
      setItems(prev => prev.map(m => m._id === id ? { ...m, status } : m));
      setSelected(prev => prev?._id === id ? { ...prev, status } : prev);
    }

    function onDeleted({ id }) {
      setItems(prev => prev.filter(m => m._id !== id));
      setSelected(prev => prev?._id === id ? null : prev);
    }

    socket.on("contact:new",     onNew);
    socket.on("contact:updated", onUpdated);
    socket.on("contact:deleted", onDeleted);

    return () => {
      socket.off("contact:new",     onNew);
      socket.off("contact:updated", onUpdated);
      socket.off("contact:deleted", onDeleted);
    };
  }, [socketRef]);
  // ─────────────────────────────────────────────────────────────────

  const updateStatus = async (id, status) => {
    try {
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/contacts/${id}`, {
        method: "PATCH", body: JSON.stringify({ status }),
      });
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Status update failed.");
      setSelected(prev => prev?._id === id ? { ...prev, status } : prev);
      loadItems(page, search, filter, dateFrom, dateTo);
    } catch (err) { alert(err.message); }
  };

  const deleteItem = async (id) => {
    try {
      const res = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/contacts/${id}`, { method: "DELETE" });
      if (res.status === 401) { localStorage.removeItem("paladin_admin_token"); navigate("/admin/login"); return; }
      if (!res.ok) throw new Error("Delete failed.");
      loadItems(page, search, filter, dateFrom, dateTo);
    } catch (err) { alert(err.message); }
  };

  const exportCSV = async () => {
    const params = new URLSearchParams({ page: 1, limit: 1000 });
    if (search) params.set("search", search);
    if (filter && filter !== "all") params.set("status", filter);
    if (dateFrom) params.set("from", dateFrom);
    if (dateTo)   params.set("to", dateTo);
    const res  = await adminFetch(`${process.env.REACT_APP_API_URL}/api/admin/contacts?${params}`);
    const json = await res.json();
    const all  = json.data || [];
    const headers = ["Name", "Email", "Subject", "Message", "Status", "Received"];
    const escape = v => { const s = v == null ? "" : String(v); return s.includes(",") || s.includes('"') || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = all.map(m => [m.name, m.email, m.subject, m.message, m.status, new Date(m.createdAt).toLocaleDateString("en-US")]);
    const csv = [headers.join(","), ...rows.map(r => r.map(escape).join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = "contacts.csv"; a.click();
  };

  const paginated    = items;
  const safePage     = page;
  const selectedLive = selected ? items.find(m => m._id === selected._id) || selected : null;

  if (loading) return <Spinner />;
  if (error) return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "240px", gap: "12px" }}>
      <p style={{ fontFamily: "'Constantia', serif", color: "#dc2626", fontSize: "14px" }}>{error}</p>
      <button onClick={() => window.location.reload()} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#012E72", border: "1px solid rgba(1,46,114,0.25)", borderRadius: "999px", padding: "8px 20px", cursor: "pointer", backgroundColor: "transparent" }}>Retry</button>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "12px" }}>
        <div>
          <p style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.26em", textTransform: "uppercase", color: "rgba(1,46,114,0.35)", marginBottom: "6px" }}>Admin / Messages</p>
          <h1 style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "24px", letterSpacing: "0.04em", color: "#012E72", lineHeight: 1 }}>Contact Messages</h1>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", paddingBottom: "2px" }}>
            {total} total · {counts.unread || 0} unread
          </p>
          {items.length > 0 && (
            <button onClick={exportCSV} style={{
              display: "flex", alignItems: "center", gap: "6px",
              fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase",
              color: "#012E72", border: "1px solid rgba(1,46,114,0.2)", borderRadius: "999px",
              padding: "8px 16px", cursor: "pointer", backgroundColor: "transparent", transition: "all 0.15s",
            }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = "#012E72"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#012E72"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "transparent"; e.currentTarget.style.color = "#012E72"; e.currentTarget.style.borderColor = "rgba(1,46,114,0.2)"; }}
            >
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* ── Stat pills ── */}
      <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
        {[
          { label: "Total",   value: total,              color: "#012E72", bg: "rgba(1,46,114,0.07)" },
          { label: "Unread",  value: counts.unread  || 0, color: "#92400E", bg: "#FEF3C7" },
          { label: "Read",    value: counts.read    || 0, color: "#1D4ED8", bg: "#DBEAFE" },
          { label: "Replied", value: counts.replied || 0, color: "#065F46", bg: "#D1FAE5" },
        ].map(s => (
          <div key={s.label} style={{ backgroundColor: s.bg, borderRadius: "10px", padding: "10px 18px", display: "flex", alignItems: "center", gap: "10px" }}>
            <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "18px", color: s.color }}>{s.value}</span>
            <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.16em", textTransform: "uppercase", color: s.color, opacity: 0.7 }}>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ── Search + Filters ── */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: "200px", maxWidth: "300px" }}>
          <svg style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
            width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text" placeholder="Search name, email, subject…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ width: "100%", paddingLeft: "34px", paddingRight: search ? "32px" : "14px", paddingTop: "9px", paddingBottom: "9px", fontFamily: "'Constantia', serif", fontSize: "13px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "999px", backgroundColor: "#fff", outline: "none", boxSizing: "border-box" }}
            onFocus={e => e.target.style.borderColor = "#002DB5"}
            onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"}
          />
          {search && (
            <button onClick={() => setSearch("")} style={{ position: "absolute", right: "12px", top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#9CA3AF", fontSize: "16px", lineHeight: 1 }}>×</button>
          )}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "999px", padding: "8px 12px", outline: "none", backgroundColor: "#fff", cursor: "pointer" }}
            onFocus={e => e.target.style.borderColor = "#002DB5"} onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"} />
          <span style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", color: "#94a3b8" }}>to</span>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "#012E72", border: "1px solid rgba(1,46,114,0.13)", borderRadius: "999px", padding: "8px 12px", outline: "none", backgroundColor: "#fff", cursor: "pointer" }}
            onFocus={e => e.target.style.borderColor = "#002DB5"} onBlur={e => e.target.style.borderColor = "rgba(1,46,114,0.13)"} />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.1em", textTransform: "uppercase", color: "#94a3b8", background: "none", border: "none", cursor: "pointer" }}>×</button>
          )}
        </div>

        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {["all", ...STATUS_OPTIONS].map(s => {
            const active = filter === s;
            const count  = s === "all" ? total : counts[s] || 0;
            return (
              <button key={s} onClick={() => setFilter(s)} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", padding: "8px 16px", borderRadius: "999px", cursor: "pointer", transition: "all 0.15s", backgroundColor: active ? "#012E72" : "#fff", color: active ? "#fff" : "#6B7280", border: `1px solid ${active ? "#012E72" : "rgba(1,46,114,0.12)"}` }}>
                {s === "all" ? "All" : STATUS_CONFIG[s].label}
                {count > 0 && <span style={{ marginLeft: "6px", opacity: 0.65 }}>({count})</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Table ── */}
      <div style={{
        backgroundColor: "#fff", borderRadius: "16px", overflow: "hidden",
        border: "1px solid rgba(1,46,114,0.09)", boxShadow: "0 2px 16px rgba(1,46,114,0.06)",
      }}>
        {items.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "64px 32px", gap: "10px" }}>
            <div style={{ width: "48px", height: "48px", borderRadius: "12px", backgroundColor: "rgba(1,46,114,0.06)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.35)" strokeWidth="1.5">
                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
              </svg>
            </div>
            <p style={{ fontFamily: "'Cinzel', serif", fontSize: "13px", color: "#012E72" }}>No messages found</p>
            <p style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.4)", fontStyle: "italic" }}>
              {search ? `No matches for "${search}"` : "No contact messages here yet."}
            </p>
            {search && (
              <button onClick={() => setSearch("")} style={{ fontFamily: "'Cinzel', serif", fontSize: "9px", letterSpacing: "0.15em", textTransform: "uppercase", color: "#012E72", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", opacity: 0.6 }}>Clear search</button>
            )}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid rgba(1,46,114,0.07)" }}>
                  {["Name", "Email", "Subject", "Received", "Status", ""].map((h, i) => (
                    <th key={i} style={{ padding: "13px 18px", textAlign: "left", fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.18em", textTransform: "uppercase", color: "rgba(1,46,114,0.38)", backgroundColor: "rgba(1,46,114,0.025)", whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((m, i) => {
                  const cfg      = STATUS_CONFIG[m.status] || {};
                  const isUnread = m.status === "unread";
                  return (
                    <tr
                      key={m._id}
                      onClick={() => setSelected(m)}
                      style={{ borderBottom: i < paginated.length - 1 ? "1px solid rgba(1,46,114,0.055)" : "none", cursor: "pointer", transition: "background-color 0.12s", backgroundColor: isUnread ? "rgba(254,243,199,0.25)" : "transparent" }}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = "rgba(1,46,114,0.025)"}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = isUnread ? "rgba(254,243,199,0.25)" : "transparent"}
                    >
                      <td style={{ padding: "15px 18px", whiteSpace: "nowrap" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          {isUnread && <span style={{ width: "7px", height: "7px", borderRadius: "50%", backgroundColor: "#F59E0B", flexShrink: 0 }} />}
                          <span style={{ fontFamily: "'Cinzel', serif", fontWeight: 700, fontSize: "13px", color: "#012E72" }}>{m.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: "15px 18px", maxWidth: "180px" }}>
                        <span style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: "rgba(1,46,114,0.55)", display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.email}</span>
                      </td>
                      <td style={{ padding: "15px 18px", maxWidth: "240px" }}>
                        <span style={{ fontFamily: "'Constantia', serif", fontSize: "13px", color: isUnread ? "#012E72" : "rgba(1,46,114,0.55)", fontWeight: isUnread ? 600 : 400, display: "block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.subject}</span>
                      </td>
                      <td style={{ padding: "15px 18px", whiteSpace: "nowrap" }}>
                        <span style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.38)" }}>{timeAgo(m.createdAt)}</span>
                      </td>
                      <td style={{ padding: "15px 18px" }}>
                        <span style={{ fontFamily: "'Cinzel', serif", fontSize: "8px", letterSpacing: "0.1em", textTransform: "uppercase", color: cfg.color, backgroundColor: cfg.bg, borderRadius: "6px", padding: "4px 10px", whiteSpace: "nowrap" }}>{cfg.label}</span>
                      </td>
                      <td style={{ padding: "15px 14px" }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="rgba(1,46,114,0.25)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="9 18 15 12 9 6"/>
                        </svg>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Pagination ── */}
      {totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.4)" }}>
            Showing {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, total)} of {total}
          </p>
          <div style={{ display: "flex", gap: "6px" }}>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cinzel', serif", fontSize: "13px", border: "1px solid rgba(1,46,114,0.12)", color: "#012E72", backgroundColor: "#fff", cursor: safePage === 1 ? "not-allowed" : "pointer", opacity: safePage === 1 ? 0.35 : 1 }}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(n => n === 1 || n === totalPages || Math.abs(n - safePage) <= 1)
              .reduce((acc, n, idx, arr) => { if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…"); acc.push(n); return acc; }, [])
              .map((n, i) => n === "…" ? (
                <span key={`e-${i}`} style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Constantia', serif", fontSize: "12px", color: "rgba(1,46,114,0.3)" }}>…</span>
              ) : (
                <button key={n} onClick={() => setPage(n)} style={{ width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cinzel', serif", fontSize: "11px", cursor: "pointer", border: `1px solid ${safePage === n ? "#012E72" : "rgba(1,46,114,0.12)"}`, backgroundColor: safePage === n ? "#012E72" : "#fff", color: safePage === n ? "#fff" : "#012E72" }}>{n}</button>
              ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ width: "32px", height: "32px", borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Cinzel', serif", fontSize: "13px", border: "1px solid rgba(1,46,114,0.12)", color: "#012E72", backgroundColor: "#fff", cursor: safePage === totalPages ? "not-allowed" : "pointer", opacity: safePage === totalPages ? 0.35 : 1 }}>›</button>
          </div>
        </div>
      )}

      {/* ── Detail Modal ── */}
      {selectedLive && (
        <DetailModal
          m={selectedLive}
          onClose={() => setSelected(null)}
          onStatusChange={updateStatus}
          onDelete={async (id) => { await deleteItem(id); setSelected(null); }}
          onReplySent={(id) => {
            setItems(prev => prev.map(x => x._id === id ? { ...x, status: "replied" } : x));
            setSelected(prev => prev?._id === id ? { ...prev, status: "replied" } : prev);
          }}
        />
      )}
    </div>
  );
}